import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { fetchCurrentUser, login as apiLogin, logout as apiLogout } from '@/api/auth'
import { setToken, TOKEN_KEY } from '@/api/http'

const STORAGE_KEYS = {
  token: TOKEN_KEY,
  profile: 'swy_user_profile',
}

function loadStoredProfile() {
  const raw = localStorage.getItem(STORAGE_KEYS.profile)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const useUserStore = defineStore('user', () => {
  const token = ref(localStorage.getItem(STORAGE_KEYS.token) || '')
  const profile = ref(loadStoredProfile())
  const initialized = ref(false)

  const displayName = computed(() => profile.value?.displayName || '')
  const departmentName = computed(() => profile.value?.departmentName || '')
  const positionName = computed(() => profile.value?.positionName || '')
  const role = computed(() => profile.value?.role || 'USER')
  const userId = computed(() => profile.value?.id || null)
  const isLoggedIn = computed(() => !!token.value && !!profile.value)
  const isAdmin = computed(() => role.value === 'ADMIN')

  function persistProfile(next) {
    profile.value = next
    if (next) {
      localStorage.setItem(STORAGE_KEYS.profile, JSON.stringify(next))
    } else {
      localStorage.removeItem(STORAGE_KEYS.profile)
    }
  }

  async function login(credentials) {
    const data = await apiLogin(credentials)
    token.value = data.token
    setToken(data.token)
    persistProfile(data.user)
    initialized.value = true
    return data
  }

  async function fetchProfile() {
    if (!token.value) {
      initialized.value = true
      return null
    }
    try {
      const data = await fetchCurrentUser()
      persistProfile(data)
      initialized.value = true
      return data
    } catch (error) {
      await logout(true)
      throw error
    }
  }

  async function logout(skipApi = false) {
    if (!skipApi && token.value) {
      try {
        await apiLogout()
      } catch {
        // ignore network errors during logout
      }
    }
    token.value = ''
    setToken('')
    persistProfile(null)
    initialized.value = true
  }

  function resetForTests() {
    token.value = ''
    setToken('')
    persistProfile(null)
    initialized.value = false
  }

  return {
    token,
    profile,
    initialized,
    displayName,
    departmentName,
    positionName,
    role,
    userId,
    isLoggedIn,
    isAdmin,
    login,
    fetchProfile,
    logout,
    resetForTests,
  }
})